/**
 * Symbole als kleine Komponenten statt als Bibliothek.
 *
 * Eine Symbolbibliothek kostet 20–40 kB im Startbündel; gebraucht werden knapp
 * zwanzig Zeichen. Alle folgen derselben Bauweise: 24er-Raster, Strichbreite
 * 1,75, `currentColor` — damit ein Symbol im Statusfeld automatisch die
 * Schriftfarbe des Feldes annimmt (Kapitel 44: Farbe nie allein).
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.25em"
      height="1.25em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconHome = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z" />
  </Icon>
);

export const IconBuilding = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 21V6.5L12 3l8 3.5V21" />
    <path d="M4 21h16M9 21v-5h6v5" />
    <path d="M8.5 9h2M13.5 9h2M8.5 12.5h2M13.5 12.5h2" />
  </Icon>
);

export const IconTasks = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 5.5h3.5v3.5H4zM4 14.5h3.5V18H4z" />
    <path d="M11 7.25h9M11 16.25h9" />
  </Icon>
);

export const IconReport = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconCalendar = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 6.5h16V20H4zM4 10.5h16M8.5 4v4M15.5 4v4" />
  </Icon>
);

export const IconMessages = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 5.5h16v10H9l-5 4z" />
  </Icon>
);

export const IconMore = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="5.5" cy="12" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
    <circle cx="18.5" cy="12" r="1.25" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconMic = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 4a2.75 2.75 0 0 1 2.75 2.75v4.5a2.75 2.75 0 0 1-5.5 0v-4.5A2.75 2.75 0 0 1 12 4z" />
    <path d="M6 11.5A6 6 0 0 0 18 11.5M12 17.5V21M9 21h6" />
  </Icon>
);

export const IconCamera = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8.5h3l1.5-2h7L17 8.5h3V19H4z" />
    <circle cx="12" cy="13.5" r="3.25" />
  </Icon>
);

export const IconText = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4.5 6.5h15M4.5 11h15M4.5 15.5h10M4.5 20h6" />
  </Icon>
);

export const IconCheck = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 12.5 10 17.5 19 7" />
  </Icon>
);

export const IconClose = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

/** Vier Eckwinkel nach außen — Vollbild betreten (Screen 2f). */
export const IconExpand = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9 4.5H4.5V9M15 4.5h4.5V9M4.5 15V19.5H9M19.5 15V19.5H15" />
  </Icon>
);

export const IconAlert = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 4.5 21 19.5H3z" />
    <path d="M12 10v4" />
    <circle cx="12" cy="16.75" r="0.9" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconClock = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
);

export const IconUpload = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 16.5V5.5M8 9l4-3.5L16 9" />
    <path d="M4.5 15v3.5a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V15" />
  </Icon>
);

export const IconCloudOff = (props: IconProps) => (
  <Icon {...props}>
    <path d="M17.5 17.5H7a4 4 0 0 1-.6-7.95" />
    <path d="M9.5 6.6A5 5 0 0 1 17.3 10a3.6 3.6 0 0 1 2.2 6" />
    <path d="M4 4l16 16" />
  </Icon>
);

export const IconChevronRight = (props: IconProps) => (
  <Icon {...props}>
    <path d="M9.5 5.5 16 12l-6.5 6.5" />
  </Icon>
);

export const IconChevronDown = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5.5 9.5 12 16l6.5-6.5" />
  </Icon>
);

export const IconSearch = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="6" />
    <path d="M15.5 15.5 20 20" />
  </Icon>
);

export const IconShield = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3.5 19 6v6c0 4-3 7-7 8.5C8 19 5 16 5 12V6z" />
    <path d="M9.25 12 11.5 14.25 15 10.5" />
  </Icon>
);

export const IconLayers = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 4 4 8l8 4 8-4z" />
    <path d="M4 12.5l8 4 8-4M4 16.5l8 4 8-4" />
  </Icon>
);

export const IconRoom = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 4h14v16H5z" />
    <path d="M15.5 12h.01" />
  </Icon>
);

export const IconTrash = (props: IconProps) => (
  <Icon {...props}>
    <path d="M5 7h14M9.5 7V4.5h5V7M6.5 7l.8 12.5h9.4L17.5 7" />
  </Icon>
);

export const IconPortfolio = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 4.5h7v7H4zM13 4.5h7v7h-7zM4 13.5h7v6H4zM13 13.5h7v6h-7z" />
  </Icon>
);

export const IconProjects = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 6.5h5.5l1.8 2.2H20V19H4z" />
  </Icon>
);

export const IconPlan = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 6.5 9.5 4.5 14.5 6.5 20 4.5v13L14.5 19.5 9.5 17.5 4 19.5z" />
    <path d="M9.5 4.5v13M14.5 6.5v13" />
  </Icon>
);

export const IconDocuments = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6.5 3.5h7L18 8v12.5H6.5z" />
    <path d="M13 3.5V8.5h5M9.5 12.5h5M9.5 16h5" />
  </Icon>
);

export const IconCosts = (props: IconProps) => (
  <Icon {...props}>
    <path d="M16 7.5a5.5 5.5 0 1 0 0 9" />
    <path d="M5.5 11h7M5.5 14h7" />
  </Icon>
);

export const IconReports = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4.5 19.5h15" />
    <path d="M7.5 19.5v-6M12 19.5V7M16.5 19.5v-9" />
  </Icon>
);

export const IconContacts = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="9.5" cy="9" r="3" />
    <path d="M4 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M15.5 6.75a2.75 2.75 0 0 1 0 5.5M17 14.9c2 .6 3.5 2.3 3.5 4.6" />
  </Icon>
);

export const IconSettings = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="2.75" />
    <path d="M12 4v2.2M12 17.8V20M5.1 8l1.9 1.1M17 13.9l1.9 1.1M5.1 16l1.9-1.1M17 10.1 18.9 9" />
  </Icon>
);

export const IconIssues = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 4v16" />
    <path d="M6 5h11l-2 3.5 2 3.5H6" />
  </Icon>
);

export const IconEmpty = (props: IconProps) => (
  <Icon {...props}>
    <path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z" />
    <path d="M4 8.5 12 13l8-4.5M12 13v7" />
  </Icon>
);
